import React, { useState } from 'react';
import { SdmSpacer, SdmTypography, SdmTextButton, SdmSelect } from 'components';
import Dialog from '@material-ui/core/Dialog';
import Grid from '@material-ui/core/Grid';
import IconButton from '@material-ui/core/IconButton';
import CloseIcon from '@material-ui/icons/Close';
import { styled } from '@material-ui/core/styles';
import { withStyles } from '@material-ui/styles';
import { TextField } from '@material-ui/core';
import { Controller, useForm } from 'react-hook-form';
import { theme } from 'theme';

import {
  format,
  formatISO,
  intlFormat,
  isAfter,
  setHours,
  setMinutes,
} from 'date-fns';
import { useDataHelper } from 'utils/dataHelper';
import TimeInput from '../TimeInput/TimeInput';
import { AgendaVisitEndHour, AgendaVisitStartHour } from 'utils/constants';

const StyledIconButton = styled(IconButton)({
  padding: 5,
});

const CustomFormTextField = withStyles({
  root: {
    width: '95%',
    '& .MuiFormControl-root': {
      width: '95%',
    },
    '& label.Mui-focused': {
      color: `${theme.colors.primary.black}`,
    },
    '& .MuiInput-underline:after': {
      borderBottomColor: `${theme.colors.primary.black}`,
    },
    '& .MuiInput-underline:before': {
      borderBottom: `2px solid ${theme.colors.primary.black}`,
    },
    '& .MuiInputBase-input': {
      fontFamily: 'Suisse-Intl',
      fontSize: 26,
    },
    '& .MuiFormLabel-root': {
      fontFamily: 'Suisse-Intl',
      color: `${theme.colors.primary.black}`,
      fontSize: 21,
    },
  },
})(TextField);

const appForSlotsOptions = [
  { value: 1, label: 1 },
  { value: 2, label: 2 },
  { value: 3, label: 3 },
  { value: 4, label: 4 },
  { value: 5, label: 5 },
  { value: 6, label: 6 },
  { value: 7, label: 7 },
  { value: 8, label: 8 },
  { value: 9, label: 9 },
  { value: 10, label: 10 },
];

const defaultValue = {
  start: AgendaVisitStartHour,
  end: AgendaVisitEndHour,
  slotCapacity: undefined,
};

const AddDialog = ({
  onClose,
  onAgree,
  i18n,
  isSmDown,
  dayTime,
  appDate,
  locale,
  appointmentId,
}) => {
  // Hooks
  const [isStartSelectOpen, setIsStartSelectOpen] = useState(false);
  const [isEndSelectOpen, setIsEndSelectOpen] = useState(false);
  const [customError, setCustomError] = useState('');
  const dataHelper = useDataHelper();
  const { handleSubmit, errors, control, setValue, getValues } = useForm({
    defaultValues: defaultValue,
  });
  // Functions
  const handleOverflowBody = () => {
    const bodyStatus = document.body.getAttribute('style');
    if (bodyStatus.includes('overflow: hidden')) {
      document.body.style.overflow = 'visible';
      document.body.style.paddingTop = 79;
    }
  };

  const handleClose = () => {
    handleOverflowBody();
    onClose && onClose();
  };

  const getFullDate = (value) => {
    const hours = value.split(':')[0];
    const minutes = value.split(':')[1];
    const dateTarget = new Date();
    dateTarget.setHours(hours);
    dateTarget.setMinutes(minutes);
    return dateTarget;
  };

  const customSubmit = (data) => {
    handleOverflowBody();
    const validStart = /^([01][0-9]|2[0-3]):[0-5][0-9]$/.test(data.start);
    const validEnd = /^([01][0-9]|2[0-3]):[0-5][0-9]$/.test(data.end);
    if (!validStart || !validEnd) return;
    data.start = getFullDate(data.start);
    data.end = getFullDate(data.end);
    if (isAfter(data.end, data.start)) {
      const arrayOfKeys = ['start', 'end'];

      const timeRanges = arrayOfKeys.map((k) => {
        const clockTime = format(data[k], 'HH:mm');
        const _hours = clockTime.split(':')[0];
        const _minutes = clockTime.split(':')[1];
        const settedHours = setHours(new Date(appDate), parseInt(_hours));
        const settedMinutes = setMinutes(settedHours, parseInt(_minutes));
        return { label: k, value: formatISO(settedMinutes) };
      });
      onAgree &&
        onAgree({
          slotCapacity: data.slotCapacity.value,
          start: timeRanges.find((t) => t.label === 'start').value,
          end: timeRanges.find((t) => t.label === 'end').value,
          date: formatISO(new Date(appDate)),
          slotVisitId: appointmentId,
        });
    } else {
      return setCustomError(i18n.invalidRange);
    }
  };

  // Constants
  const date = intlFormat(
    new Date(appDate),
    {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    },
    {
      locale: dataHelper.getLangCode(locale),
    }
  );

  // Renders
  const renderError = (key, labelKey) => {
    if (errors[key] && errors[key]?.type === 'required')
      return (
        <SdmTypography
          style={{ color: `${theme.colors.primary.red}`, marginTop: 0 }}
          variant={'textBold'}
        >
          {i18n[labelKey ? labelKey : key].error}
        </SdmTypography>
      );
    return null;
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
      // maxWidth={300}
      PaperProps={{
        style: {
          paddingRight: 30,
          paddingLeft: 30,
          paddingTop: 80,
          paddingBottom: 50,
        },
      }}
    >
      <div
        tabIndex={0}
        role="button"
        onClick={handleClose}
        style={{
          position: 'absolute',
          top: 24,
          right: 24,
        }}
      >
        <StyledIconButton>
          <CloseIcon fontSize={isSmDown ? 'large' : 'small'} />
        </StyledIconButton>
      </div>
      <form onSubmit={handleSubmit(customSubmit)}>
        <Grid container>
          <Grid item xs={12}>
            <SdmTypography
              variant={'h3'}
              style={{ textTransform: 'uppercase' }}
              margin={0}
              textAlign={'center'}
            >
              {i18n.subtitle}
            </SdmTypography>
            <SdmSpacer height={20} />
            <SdmTypography variant={'h1'} margin={0} textAlign={'center'}>
              {i18n.title}
            </SdmTypography>
            <SdmSpacer height={40} />
          </Grid>
          <Grid item sm={6} xs={12}>
            <CustomFormTextField
              value={date}
              label={`${i18n.date.label}`}
              style={{ marginBottom: 30 }}
              disabled={true}
            />
          </Grid>
          <Grid item sm={6} xs={12}>
            <SdmTypography
              variant={'text'}
              margin={0}
              style={{
                width: '100%',
                color: theme.colors.primary.black,
              }}
            >
              {i18n.slotCapacity.label}*
            </SdmTypography>
            <Controller
              name={'slotCapacity'}
              control={control}
              defaultValue={appForSlotsOptions[0]}
              rules={{ required: true }}
              render={(props) => (
                <SdmSelect
                  options={appForSlotsOptions}
                  value={props.value || appForSlotsOptions[0]}
                  labelKey={'label'}
                  label={`${i18n.slotCapacity.label}*`}
                  onSelect={(e) => {
                    props.onChange(e);
                  }}
                  customStyle={{ width: '95%', marginLeft: 0, marginTop: -12 }}
                  // disabled={isFormDisabled()}
                />
              )}
            />
            {renderError('slotCapacity')}
          </Grid>
          <Grid item sm={6} xs={12}>
            <TimeInput
              control={control}
              label={`${i18n.morningStart.label}*`}
              theme={theme}
              getValues={getValues}
              setValue={setValue}
              keyName={'start'}
              customStyle={{ width: '95%', marginLeft: 0 }}
              togglingIsOpen={setIsStartSelectOpen}
              isEnd={false}
            />
            {renderError(
              'start',
              dayTime === 'am' ? 'morningStart' : 'afternoonStart'
            )}
          </Grid>
          <Grid item sm={6} xs={12}>
            <TimeInput
              control={control}
              label={`${i18n.morningEnd.label}*`}
              theme={theme}
              getValues={getValues}
              setValue={setValue}
              keyName={'end'}
              customStyle={{ width: '95%', marginLeft: 0 }}
              togglingIsOpen={setIsEndSelectOpen}
              isEnd={true}
            />
            {renderError(
              'end',
              dayTime === 'am' ? 'morningEnd' : 'afternoonEnd'
            )}
          </Grid>
          <SdmSpacer height={30} />
          <Grid item xs={12}>
            {customError && (
              <Grid item xs={12}>
                <SdmSpacer height={40} />
                <SdmTypography
                  style={{
                    color: `${theme.colors.primary.red}`,
                    marginTop: -50,
                    textAlign: 'right',
                  }}
                  variant={'textBold'}
                >
                  {customError}
                </SdmTypography>
              </Grid>
            )}
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: isSmDown ? 'center' : 'flex-end',
                // paddingRight: 75,
              }}
            >
              <SdmTextButton
                onClick={handleClose}
                autoFocus
                text={i18n.buttons.cancel}
                height={30}
                style={{ width: 158, height: 50, marginRight: 10 }}
                variant={'red'}
                textVariant={'h4Regular'}
              />
              <SdmTextButton
                type={'submit'}
                autoFocus
                text={i18n.buttons.save}
                height={30}
                style={{ width: 158, height: 50, marginLeft: 10 }}
                variant={'redFill'}
                textVariant={'h4Regular'}
              />
            </div>
          </Grid>

          <SdmSpacer height={isEndSelectOpen || isStartSelectOpen ? 190 : 0} />
        </Grid>
      </form>
    </Dialog>
  );
};

export default AddDialog;
